"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Input from "@/components/ui/input";
import { useScopedI18n } from "@/messages/client";
import { Column } from "@tanstack/react-table";
import { useDebounce } from "ahooks";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";

interface ColumnFilterValue {
  search: string;
  newProduct: boolean;
}

export default function ProductFilter<T>({
  column,
}: {
  column: Column<T, unknown>;
}) {
  const t = useScopedI18n("选型器");

  const columnFilterValue = (column.getFilterValue() as ColumnFilterValue) ?? { search: "", newProduct: false };

  const [search, setSearch] = useState(columnFilterValue.search);

  const debouncedValue = useDebounce(search, { wait: 300 });

  useEffect(() => {
    column.setFilterValue((prev: ColumnFilterValue) => ({
      ...prev,
      search: debouncedValue,
    }));
  }, [debouncedValue, column]);

  return (
    <div className="flex flex-col">
      <div className="flex w-full flex-col">
        <div className="mb-3 mt-4 flex items-center px-6">
          <div className="relative flex flex-1 items-center">
            <Input
              inputSize="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-full w-full rounded-sm bg-white px-1 text-xs outline-none transition-colors duration-300 focus:border-primary"
              placeholder={t("按产品型号筛选")}
              suffix={<Search size={16} />}
            />
          </div>
        </div>

        <label
          htmlFor="new-product-checkbox"
          className="flex cursor-pointer items-center space-x-2 px-6 text-sm">
          <Checkbox
            id="new-product-checkbox"
            checked={columnFilterValue.newProduct}
            onCheckedChange={(checked) => {
              column.setFilterValue((prev: ColumnFilterValue) => ({
                ...prev,
                newProduct: checked,
              }));
            }}
          />
          <span>{t("查看新产品")}</span>
        </label>
      </div>
      <div className="p-4">
        <Button
          className="h-auto p-0 text-sm font-normal text-primary hover:bg-transparent"
          variant="ghost"
          onClick={() => {
            setSearch("");
            column.setFilterValue("");
          }}>
          {t("复位")}
        </Button>
      </div>
    </div>
  );
}
